import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { MarkerNoteCard } from "components/SmartSurveyor/shared/MarkerNoteCard";
import { ZOOM_CONFIG } from "../../constants";
import {
  STICKY_NOTE_MAX_WIDTH_PX,
  STICKY_NOTE_MIN_WIDTH_PX,
  STICKY_NOTE_TAIL_DROP_PX,
  STICKY_NOTE_TAIL_INSET_PX,
} from "./constants";
import type { MouseEvent, TouchEvent } from "react";
import type { StickyNoteProps } from "./types";

// A press on the card selects its marker; it must not also start a pan or a marker pick underneath.
const stopPlanTouch = (event: TouchEvent) => {
  event.stopPropagation();
};

// Also blocks the text selection a drag across the card would otherwise start.
const stopPlanMouseDown = (event: MouseEvent) => {
  event.stopPropagation();
  event.preventDefault();
};

export const StickyNote = memo(function StickyNote({
  note,
  rgb,
  anchorX,
  anchorY,
  objectId,
  onSelectNote,
  shouldAnimateTransition,
}: StickyNoteProps) {
  const { t } = useTranslation();

  const selectMarker = useCallback(() => {
    onSelectNote?.(objectId);
  }, [onSelectNote, objectId]);

  const isSelectable = onSelectNote !== undefined;

  return (
    // Moves with the same transition as the plan so the tail stays on the marker while panning.
    <div
      className="absolute left-0 top-0"
      style={{
        transform: `translate(${anchorX}px, ${anchorY}px)`,
        transition: shouldAnimateTransition
          ? ZOOM_CONFIG.PAN_TRANSITION
          : "none",
      }}
    >
      <div
        className={clsx(
          "absolute w-max",
          isSelectable &&
            "pointer-events-auto cursor-pointer transition hover:brightness-95",
        )}
        style={{
          left: -STICKY_NOTE_TAIL_INSET_PX,
          bottom: STICKY_NOTE_TAIL_DROP_PX,
          minWidth: STICKY_NOTE_MIN_WIDTH_PX,
          maxWidth: STICKY_NOTE_MAX_WIDTH_PX,
        }}
        // Mouse users click anywhere on the card; keyboard and screen readers use the hidden button below.
        role={isSelectable ? "presentation" : undefined}
        onMouseDown={isSelectable ? stopPlanMouseDown : undefined}
        onTouchStart={isSelectable ? stopPlanTouch : undefined}
        onClick={isSelectable ? selectMarker : undefined}
      >
        {isSelectable && (
          // Its click bubbles to the card's handler, so it needs none of its own.
          <button type="button" className="peer sr-only">
            {t("smartSurveyor.elementDetails.note.showMarker")}
          </button>
        )}
        <div className="rounded-xl peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2">
          <MarkerNoteCard note={note} rgb={rgb} />
        </div>
      </div>
    </div>
  );
});
